import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';

interface Stats {
  total: number;
  confirmed: number;
  pending: number;
  totalAmount: number;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'operator') {
      loadStats();
    } else {
      setLoading(false);
    }
  }, [user?.role]); // Загружаем только при изменении роли пользователя

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  if (user?.role !== 'operator') {
    return <div className="text-center text-red-600">Доступ запрещен</div>;
  }

  const operatorPages = [
    { 
      name: 'Платежные заявки', 
      description: 'Просмотр и управление платежными заявками',
      icon: '💳',
      path: '/payment-requests',
      color: 'bg-blue-500'
    },
    { 
      name: 'Платежи', 
      description: 'Просмотр и обработка платежей',
      icon: '💵',
      path: '/payments',
      color: 'bg-indigo-500'
    },
    { 
      name: 'Вывод средств', 
      description: 'Обработка запросов на вывод средств',
      icon: '💸',
      path: '/withdrawals',
      color: 'bg-emerald-500'
    },
    { 
      name: 'Реквизиты оплаты', 
      description: 'Просмотр доступных реквизитов для оплаты',
      icon: '🔧',
      path: '/payment-requisites',
      color: 'bg-green-500'
    },
    { 
      name: 'Покупатели', 
      description: 'Информация о покупателях',
      icon: '👤',
      path: '/buyers',
      color: 'bg-purple-500'
    },
    { 
      name: 'Аудит лог', 
      description: 'История действий в системе',
      icon: '📝',
      path: '/audit-logs',
      color: 'bg-yellow-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Заголовок с навигацией */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Панель оператора</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Управление платежами, реквизитами и мониторинг системы
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  Пользователь: <span className="font-medium">{user.username}</span>
                </span>
                <button
                  onClick={logout}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Выйти
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Приветствие */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Добро пожаловать, {user?.username}!
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Роль: Оператор магазина
            </p>
          </div>
        </div>

        {/* Навигация по страницам */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {operatorPages.map((page) => (
            <Link
              key={page.path}
              to={page.path}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 ${page.color} rounded-md flex items-center justify-center`}>
                      <span className="text-white text-lg">{page.icon}</span>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{page.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{page.description}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Статистика (если доступна) */}
        {stats && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Статистика</h3>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="flex items-center min-h-[4rem]">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">₽</span>
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">Всего платежей</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                  </div>
                </div>

                <div className="flex items-center min-h-[4rem]">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">✓</span>
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">Подтверждено</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.confirmed}</dd>
                  </div>
                </div>

                <div className="flex items-center min-h-[4rem]">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">⏳</span>
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">Ожидает</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.pending}</dd>
                  </div>
                </div>

                <div className="flex items-center min-h-[4rem]">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">💰</span>
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">Общая сумма</dt>
                    <dd className="text-lg font-medium text-gray-900">{(stats.totalAmount / 100).toFixed(2)} ₽</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
